/**
 * Modelos de las pantallas (puros): a partir del libro leído y del dominio. Equivalen a lo que calculaba
 * el servidor de Apps Script (buildDashboardModel_, buildMonthTransactions_), ahora en el dispositivo.
 */
import {
  effectiveMovements, effectiveParts, attributedItems, monthTotals, categoryTotals, budgetReport, cashAt, netWorthSeries, investedUntil, monthEnd, prevMonth, round2,
  type EffectiveMovement, type EffectivePart, type Attributed, type BudgetLine, type NetWorthPoint, type MonthTotals,
} from '@pf/domain';
import { ADJUSTMENT_SOURCE, type Workbook, type ArchivedMovement } from '@pf/workbook';
import type { BudgetRow } from '@pf/domain';
import { budgetWithPlanned } from './planned.js';
import { today as clockToday } from './clock.js';
import { poolAdjustment } from './account-pool.js';

export interface Computed {
  wb: Workbook;
  movements: EffectiveMovement[];
  parts: EffectivePart[];
  items: Attributed[];
  months: string[];
  byId: Map<string, EffectiveMovement>;
  partsBy: Map<string, EffectivePart[]>;
  groups: Map<string, EffectiveMovement[]>;
  /** Presupuesto con los gastos previstos «Suma» añadidos (para mostrar y comparar; lo guardado es wb.budget). */
  budget: BudgetRow[];
}

export function compute(wb: Workbook): Computed {
  const movements = effectiveMovements(wb.movements, wb.categories);
  const parts = effectiveParts(wb.parts, movements, wb.categories);
  const items = attributedItems(movements, parts);
  const months = [...new Set(items.map(i => i.month).filter((m): m is string => !!m))].sort().reverse();
  const partsBy = new Map<string, EffectivePart[]>();
  parts.forEach(p => partsBy.set(p.movementId, [...(partsBy.get(p.movementId) ?? []), p]));
  const groups = new Map<string, EffectiveMovement[]>();
  movements.forEach(m => { if (m.group) { const k = m.group.toLowerCase(); groups.set(k, [...(groups.get(k) ?? []), m]); } });
  return { wb, movements, parts, items, months, byId: new Map(movements.map(m => [m.id, m])), partsBy, groups, budget: budgetWithPlanned(wb.budget, wb.planned ?? []) };
}

export interface CategoryLine { category: string; amount: number; prevAmount: number; count: number }
export interface AccountLine { name: string; share: number; recorded: number; atBankDate: number; bankBalance: number | null; bankBalanceDate: string; status: 'ok' | 'diff' | 'unknown'; diff: number | null }
export interface Dashboard {
  month: string;
  current: MonthTotals;
  previous: MonthTotals & { month: string };
  expensesByCategory: CategoryLine[];
  incomeByCategory: CategoryLine[];
  budget: BudgetLine[];
  alerts: BudgetLine[];
  accounts: AccountLine[];
  netWorth: { cash: number; investments: number; total: number };
  investments: { invested: number; value: number; gain: number; returnPct: number | null };
  netWorthHistory: NetWorthPoint[];
  netWorthChange: number | null;
  recent: EffectiveMovement[];
}

const ULID = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';
/** Día (local) en que se anotó un movimiento, sacado de su Id (los Ids llevan la hora, estilo ULID); '' si no se sabe. */
export function recordedDay(id: string): string {
  const m = /^[A-Z]+-([0-9A-HJKMNP-TV-Z]{10})/.exec(id);
  if (!m) return '';
  const ms = [...m[1]!].reduce((t, ch) => t * 32 + ULID.indexOf(ch), 0);
  if (ms < Date.UTC(2020, 0, 1) || ms > Date.now() + 86_400_000) return '';
  const d = new Date(ms);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/**
 * Lo que la app tiene en una cuenta a una fecha: hasta ese día, más lo que el banco anota con fecha posterior pero
 * ya estaba apuntado ese día (lo pagado el fin de semana o en festivo se anota el siguiente día hábil, pero el saldo
 * del banco de ese día ya lo descuenta). Si la fecha es hoy (o posterior), todo lo apuntado.
 */
export function balanceAt(c: Computed, account: string, date: string, today: string): number {
  const a = c.wb.accounts.find(x => x.name === account);
  const upTo = date >= today ? '9999-12-31' : date;
  const known = (m: Workbook['movements'][number]) => { const r = recordedDay(m.id); return !!r && r <= date; };
  return round2((a ? a.opening + a.archived : 0) + c.wb.movements.filter(m => m.account === account && m.date && (m.date <= upTo || known(m))).reduce((t, m) => t + m.amount, 0));
}

/**
 * Saldo de cada cuenta y si cuadra con el banco. El saldo del banco es de un día concreto, así que se compara con
 * lo que la app tiene ese día (ver balanceAt: incluye lo ya apuntado ese día con fecha posterior).
 */
export function accountLines(c: Computed, today = clockToday()): AccountLine[] {
  return c.wb.accounts.map(a => {
    const mine = c.wb.movements.filter(m => m.account === a.name);
    const base = a.opening + a.archived;
    const recorded = round2(base + mine.reduce((t, m) => t + m.amount, 0));
    const atBankDate = a.bankBalanceDate ? balanceAt(c, a.name, a.bankBalanceDate, today) : recorded;
    const diff = a.bankBalance === null ? null : round2(a.bankBalance - atBankDate);
    return { name: a.name, share: a.share, recorded, atBankDate, bankBalance: a.bankBalance, bankBalanceDate: a.bankBalanceDate,
      status: diff === null ? 'unknown' : Math.abs(diff) < 0.01 ? 'ok' : 'diff', diff };
  });
}

/** Extracto de una cuenta: movimientos en orden (fecha y fila de la hoja) con el saldo tras cada uno. */
export function accountStatement(c: Computed, account: string) {
  const a = c.wb.accounts.find(x => x.name === account);
  let balance = a ? a.opening + a.archived : 0;
  return c.wb.movements.filter(m => m.account === account)
    .sort((x, y) => x.date.localeCompare(y.date) || x.row - y.row)
    .map(m => { balance += m.amount; return { id: m.id, date: m.date, concept: m.concept, amount: m.amount, note: m.note, balance: round2(balance) }; });
}

export function investments(c: Computed) {
  const units = new Map<string, number>();
  // Como SUMIFS en la hoja: el nombre del fondo no distingue mayúsculas
  c.wb.flows.forEach(f => units.set(f.fund.toLowerCase(), (units.get(f.fund.toLowerCase()) ?? 0) + (f.units ?? 0)));
  const invested = round2(c.wb.flows.reduce((s, f) => s + f.amount, 0));
  const value = round2(c.wb.positions.reduce((s, p) => s + (p.price === null ? 0 : (units.get(p.fund.toLowerCase()) ?? 0) * p.price), 0));
  const gain = round2(value - invested);
  return { invested, value, gain, returnPct: invested > 0 ? Math.round(gain / invested * 1000) / 10 : null };
}

function categoryLines(c: Computed, month: string, kind: 'expense' | 'income'): CategoryLine[] {
  const cur = categoryTotals(c.items.filter(i => i.kind === kind), month);
  const prev = categoryTotals(c.items.filter(i => i.kind === kind), prevMonth(month));
  const count = new Map<string, number>();
  c.items.forEach(i => { if (i.month === month && i.kind === kind && i.attributable) count.set(i.category, (count.get(i.category) ?? 0) + 1); });
  return [...cur.entries()].filter(([, v]) => Math.abs(v) >= 0.005)
    .map(([category, amount]) => ({ category, amount, prevAmount: prev.get(category) ?? 0, count: count.get(category) ?? 0 }))
    .sort((a, b) => Math.abs(b.amount) - Math.abs(a.amount));
}

/**
 * Fotos de patrimonio tal como se muestran. La foto solo es imprescindible para el valor de los fondos:
 * - Liquidez: se recalcula SIEMPRE con los movimientos (así las conciliaciones e importaciones tardías se reflejan),
 *   salvo en meses corregidos a mano ('manual') o anteriores al primer movimiento del libro.
 * - Inversiones: en meses estimados, lo aportado hasta fin de mes con las aportaciones actuales del libro.
 */
export function effectiveSnapshots(c: Computed) {
  const firstMonth = c.wb.movements.reduce((m, x) => (x.date && (!m || x.date < m) ? x.date : m), '').slice(0, 7);
  const accounts = c.wb.accounts.map(a => ({ name: a.name, share: a.share, inNetWorth: a.inNetWorth, opening: a.opening + a.archived }));
  const movements = c.wb.movements.map(m => ({ account: m.account, date: m.date, amount: m.amount }));
  return c.wb.snapshots.map(s => ({
    ...s,
    // Con cuentas apartadas «según lo que aporta cada persona», tu parte de verdad (no el % del saldo)
    cash: s.source !== 'manual' && firstMonth && s.month >= firstMonth ? round2(cashAt(accounts, movements, monthEnd(s.month)) + poolAdjustment(c, monthEnd(s.month))) : s.cash,
    investments: s.source === 'estimated' && c.wb.flows.length ? investedUntil(c.wb.flows, monthEnd(s.month)) : s.investments,
  }));
}

/** Ajustes de saldo (origen «ajuste» en _Meta) de las cuentas del patrimonio, en tu parte: no son ahorro. */
export function adjustmentFlows(c: Computed) {
  const ids = new Set(c.wb.meta.filter(m => m.source === ADJUSTMENT_SOURCE).map(m => m.id));
  const acc = new Map(c.wb.accounts.map(a => [a.name, a]));
  return c.wb.movements.filter(m => ids.has(m.id) && acc.get(m.account)?.inNetWorth).map(m => ({ date: m.date, amount: round2(m.amount * (acc.get(m.account)!.share || 1)) }));
}

export function dashboard(c: Computed, month: string, todayIso: string): Dashboard {
  const accounts = accountLines(c);
  const inv = investments(c);
  const cash = cashAt(c.wb.accounts.map(a => ({ name: a.name, share: a.share, inNetWorth: a.inNetWorth, opening: a.opening + a.archived })),
    c.wb.movements.map(m => ({ account: m.account, date: m.date, amount: m.amount })), todayIso) + poolAdjustment(c, todayIso);
  const snapshots = effectiveSnapshots(c);
  const series = netWorthSeries(snapshots, { month: todayIso.slice(0, 7), cash, investments: inv.value }, c.wb.flows, adjustmentFlows(c));
  const budget = budgetReport(c.items, c.budget, c.wb.categories, month).filter(l => l.budget !== null);
  const pm = prevMonth(month);
  return {
    month,
    current: monthTotals(c.items, month),
    previous: { month: pm, ...monthTotals(c.items, pm) },
    expensesByCategory: categoryLines(c, month, 'expense'),
    incomeByCategory: categoryLines(c, month, 'income'),
    budget,
    alerts: budget.filter(l => l.warn && l.over),
    accounts,
    netWorth: { cash, investments: inv.value, total: round2(cash + inv.value) },
    investments: inv,
    netWorthHistory: series.slice(-36),
    netWorthChange: series.length ? series[series.length - 1]!.change : null,
    recent: c.movements.slice().sort((a, b) => b.date.localeCompare(a.date)).slice(0, 5),
  };
}

/** Movimientos de un mes (o de una categoría en un mes), con sus partes y sus reembolsos. */
export interface MovementRow {
  m: EffectiveMovement;
  parts: EffectivePart[];
  /** Reembolsos enlazados (solo en el gasto principal de su grupo). */
  reimbursements: EffectiveMovement[];
  /** Si es un reembolso: el gasto que reembolsa. */
  reimburses: EffectiveMovement | null;
  /** Gasto que comparte grupo con otro gasto (sus reembolsos se cuentan en el otro): suele ser un error. */
  extraExpense: boolean;
  /** Importe a mostrar: en una categoría, lo que te ha costado (tras reembolsos y parte compartida). */
  amountShown: number;
}

/** Gasto principal de un grupo: el primero con categoría, igual que la regla de herencia del libro. */
export function primaryExpense(c: Computed, group: string): EffectiveMovement | null {
  if (!group) return null;
  return (c.groups.get(group.toLowerCase()) ?? []).find(x => x.amount < 0 && !!x.category) ?? null;
}

function rowFor(c: Computed, m: EffectiveMovement, parts: EffectivePart[]): Omit<MovementRow, 'amountShown'> {
  const primary = primaryExpense(c, m.group);
  const isPrimary = !!primary && primary.id === m.id;
  return {
    m, parts,
    reimbursements: isPrimary ? (c.groups.get(m.group.toLowerCase()) ?? []).filter(x => x.amount > 0) : [],
    reimburses: m.amount > 0 && primary ? primary : null,
    extraExpense: m.amount < 0 && !!primary && !isPrimary,
  };
}

/**
 * Lo que te cuesta un gasto con reembolsos: lo que cuenta para ti de este cargo más lo que cuentan sus reembolsos,
 * los dos con la mitad si es compartido (los reembolsos heredan el «compartido» del gasto). Es lo mismo que suma su categoría.
 */
export function costOf(m: Pick<EffectiveMovement, 'attributable'>, reimbursements: Array<Pick<EffectiveMovement, 'attributable'>>): { charged: number; reimbursed: number; net: number } {
  const reimbursed = round2(reimbursements.reduce((t, x) => t + x.attributable, 0));
  return { charged: m.attributable, reimbursed, net: round2(m.attributable + reimbursed) };
}

/** Fila de un movimiento suelto (Resumen, cuentas). */
export function rowOf(c: Computed, m: EffectiveMovement): MovementRow {
  return { ...rowFor(c, m, c.partsBy.get(m.id) ?? []), amountShown: m.amount };
}

export function monthMovements(c: Computed, month: string, category?: string): MovementRow[] {
  const rows: MovementRow[] = [];
  for (const m of c.movements) {
    const parts = c.partsBy.get(m.id) ?? [];
    const inMonth = m.effMonth === month || parts.some(p => p.effMonth === month);
    if (!inMonth) continue;
    if (!category) { rows.push({ ...rowFor(c, m, parts), amountShown: m.amount }); continue; }
    const own = m.effCategory === category && m.kind !== 'excluded';
    const part = parts.filter(p => p.category === category);
    if (!own && !part.length) continue;
    const base = rowFor(c, m, part);
    // Lo que te ha costado: el gasto más sus reembolsos (que cuentan en su misma categoría y mes)
    const reimb = own ? base.reimbursements.filter(x => x.effCategory === category && x.effMonth === month) : [];
    const shown = own ? m.attributable + reimb.reduce((t, x) => t + x.attributable, 0) : part.reduce((t, p) => t + p.attributable, 0);
    rows.push({ ...base, reimbursements: reimb, amountShown: round2(shown) });
  }
  if (category) {
    // Los reembolsos que ya se ven dentro de su gasto no se repiten como filas sueltas
    const shownInside = new Set(rows.flatMap(r => r.reimbursements.map(x => x.id)));
    return rows.filter(r => !shownInside.has(r.m.id)).sort((a, b) => b.m.date.localeCompare(a.m.date));
  }
  return rows.sort((a, b) => b.m.date.localeCompare(a.m.date));
}

export interface SearchQuery { text: string; from?: string; to?: string; category?: string; account?: string; min?: number | null; max?: number | null; sign?: '' | 'expense' | 'income'; hideTransfers?: boolean }

/** Traspasos entre cuentas propias (categoría "Transferencia interna"): no son dinero que entra o sale. */
export const isInternalTransfer = (category: string) => /^transferencia interna$/i.test(category.trim());

/** Búsqueda en movimientos actuales y archivados: texto (concepto, nota, grupo) o importe exacto, fechas, categoría, cuenta, importe mínimo/máximo (en valor absoluto) y tipo. */
export function search(c: Computed, archive: ArchivedMovement[], q: SearchQuery) {
  const t = q.text.trim().toLowerCase();
  const amount = /^-?\d+([.,]\d{1,2})?$/.test(t) ? Number(t.replace(',', '.')) : null;
  const match = (x: { date: string; concept: string; note: string; group: string; amount: number; category: string; account: string }) =>
    (!t || (amount !== null ? Math.abs(Math.abs(x.amount) - Math.abs(amount)) < 0.005 : `${x.concept} ${x.note} ${x.group}`.toLowerCase().includes(t)))
    && (!q.from || x.date >= q.from) && (!q.to || x.date <= q.to) && (!q.category || x.category === q.category) && (!q.account || x.account === q.account)
    && (q.min === null || q.min === undefined || Math.abs(x.amount) >= q.min - 0.005) && (q.max === null || q.max === undefined || Math.abs(x.amount) <= q.max + 0.005)
    && (!q.sign || (q.sign === 'expense' ? x.amount < 0 : x.amount > 0))
    && (!q.hideTransfers || !isInternalTransfer(x.category) || q.category === x.category);
  const current = c.movements.map(m => ({ id: m.id, date: m.date, concept: m.concept, note: m.note, group: m.group, amount: m.amount, category: m.effCategory, account: m.account, archived: false }));
  const old = archive.map(a => ({ id: a.id, date: a.date, concept: a.concept, note: a.note, group: a.group, amount: a.amount, category: a.category, account: a.account, archived: true }));
  return current.concat(old).filter(match).sort((a, b) => b.date.localeCompare(a.date)).slice(0, 300);
}
